import {
  APPLY_CHUNK_SIZE,
  officeIdentityBackfillService,
  type ApplyMode,
  type ApplyRow,
  type ApplyRowResult,
} from "../../../api/officeIdentityBackfill.service.js";

/**
 * Everything a chunked run produced: the per-row results that came back, and
 * the failure that ended it early — or null for a run that sent every chunk.
 * A partial run's results are the most valuable thing it has (the rollback CSV
 * is built from them and stored nowhere else), so they are returned alongside
 * the failure rather than being thrown away with it.
 */
export type ChunkedApplyOutcome = {
  results: ApplyRowResult[];
  failure: string | null;
};

/**
 * Runs the rows through the apply endpoint in chunks of APPLY_CHUNK_SIZE,
 * sequentially, banking every chunk that comes back. Shared by the backfill
 * and restore flows — only the mode differs.
 *
 * Deliberately resolves rather than throws: a chunk that fails stops the run,
 * and the caller still gets everything the earlier chunks returned.
 */
export async function runChunkedApply(
  rows: ApplyRow[],
  mode: ApplyMode,
  onProgress: (done: number, total: number) => void,
): Promise<ChunkedApplyOutcome> {
  const results: ApplyRowResult[] = [];
  onProgress(0, rows.length);

  for (let start = 0; start < rows.length; start += APPLY_CHUNK_SIZE) {
    const chunk = rows.slice(start, start + APPLY_CHUNK_SIZE);
    try {
      const response = await officeIdentityBackfillService.apply(chunk, mode);
      results.push(...response.results);
    } catch (applyError) {
      const message = applyError instanceof Error ? applyError.message : String(applyError);
      // A request that failed may still have written rows: a gateway timeout
      // cuts the connection, not the transaction the API is part-way through.
      return {
        results,
        failure:
          `Stopped after ${results.length.toLocaleString()} of ` +
          `${rows.length.toLocaleString()} rows: ${message} ` +
          "Rows in the failed batch may or may not have been written — " +
          "re-scan before retrying.",
      };
    }
    onProgress(Math.min(start + chunk.length, rows.length), rows.length);
  }

  return { results, failure: null };
}
